import { ControlBase } from "../../controls";
import { getFromDataPath } from "../../utility";
import { CaseWhenItem, PATCH_ARS_STATE, useArsContext } from "../contexts";
import { isCaseWhenItem } from "../typeGuards";
import * as styles from "./CaseWhen.module.css";
import { RemoveArsItemButton } from "./RemoveArsItemButton";
import { WhereClauseInput } from "./WhereClauseInput";

export interface CaseWhenItemControlProps<T> {
  id: string;
  dataPath: string;
  disabled?: boolean;
  onChange?: (newValue?: CaseWhenItem<T>) => void;
  siblingCount: number;
}

/**
 * Item control for a single CaseWhen clause
 *
 * @param props - The props for the CaseWhenItemControl component
 * @param props.id - Unique identifier for the CaseWhen item
 * @param props.dataPath - The data path for the CaseWhen item
 * @param props.disabled - Optional boolean to disable the control
 * @param props.onChange - Optional callback function to handle changes to the where clause instead of dispatching to ArsContext
 * @param props.siblingCount - Number of sibling items
 * @returns React node representing the CaseWhenItemControl
 */
export const CaseWhenItemControl = <T,>({
  id,
  dataPath,
  disabled,
  onChange,
  siblingCount,
}: CaseWhenItemControlProps<T>) => {
  const { state, dispatch } = useArsContext<T>();
  const thisCaseWhenItem: CaseWhenItem<T> | undefined = getFromDataPath(dataPath, state, (obj) =>
    isCaseWhenItem(obj, state.editorDefns.isT),
  );
  if (!thisCaseWhenItem) return null;

  return (
    <div
      id={id}
      className={styles.itemHolder}
    >
      <div
        className={styles.itemPropertyHolder}
        style={{
          flexGrow: 2,
          flexShrink: 1,
          flexBasis: 0,
          minWidth: 0,
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <WhereClauseInput
          dataPath={`${dataPath}/when`}
          disabled={disabled}
          onChange={(newValue) => {
            if (onChange) onChange({ ...thisCaseWhenItem, when: newValue });
            else
              dispatch({
                operation: PATCH_ARS_STATE,
                dataPath: `${dataPath}/when`,
                value: newValue,
              });
          }}
        />
      </div>
      <div
        className={styles.itemPropertyHolder}
        style={{ flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0 }}
      >
        <span className={styles.itemPropertyLabel}>Display</span>
        <state.editorDefns.Editor
          id={`${id}-then-display-pattern`}
          aria-label="Display pattern"
          className={styles.itemPropertyInput}
          editable={!disabled}
          setValue={(newValue) =>
            onChange
              ? onChange({ ...thisCaseWhenItem, thenDisplayPattern: newValue })
              : dispatch({
                  operation: PATCH_ARS_STATE,
                  dataPath: `${dataPath}/thenDisplayPattern`,
                  value: newValue,
                })
          }
          type="string"
          value={thisCaseWhenItem.thenDisplayPattern}
        />
      </div>
      <div
        className={styles.itemPropertyHolder}
        style={{ flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0 }}
      >
        <span className={styles.itemPropertyLabel}>Result</span>
        <ControlBase
          aria-label="Result pattern"
          disabled={disabled}
          setValue={(newValue) =>
            onChange
              ? onChange({ ...thisCaseWhenItem, thenResultPattern: newValue })
              : dispatch({
                  operation: PATCH_ARS_STATE,
                  dataPath: `${dataPath}/thenResultPattern`,
                  value: newValue,
                })
          }
          type="string"
          value={thisCaseWhenItem.thenResultPattern}
        />
        {!disabled && (
          <div className={styles.removeButtonHolder}>
            <RemoveArsItemButton
              id={`${id}-remove-button`}
              dataPath={dataPath}
              onChange={() => onChange?.()}
              siblingCount={siblingCount}
            />
          </div>
        )}
      </div>
    </div>
  );
};

CaseWhenItemControl.displayName = "CaseWhenItemControl";
